import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    TextField,
    Button,
    Typography,
    IconButton,
    Box,
    InputAdornment,
    Paper,
    MenuItem,
    CircularProgress,
} from "@mui/material";
import toast from "react-hot-toast";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import { getAllTenantsAction, tenantLoginAction, clearTenantLoginAction } from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";


// Login component with real tenants fetching
const LoginModel = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);
    const [companySelected, setCompanySelected] = useState(false);
    const [selectedLogoUrl, setSelectedLogoUrl] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Redux tenants state
    const tenantsState = useSelector((state) => state.getAllTenantsReducer || {});
    const tenants = tenantsState.successData?.tenants || tenantsState.successData?.data || [];
    const tenantsLoading = tenantsState.loading;
    const tenantsError = tenantsState.error;

    useEffect(() => {
        dispatch(getAllTenantsAction());
    }, [dispatch]);

    // phoneNumber and password integration
    const [form, setForm] = useState({ identifier: "", phoneNumber: "", password: "" });
    const [touched, setTouched] = useState({ identifier: false, phoneNumber: false, password: false });

    const handleTogglePassword = () => setShowPassword((p) => !p);

    const getInitials = (nameOrId) => {
        if (!nameOrId) return "";
        return nameOrId
            .toString()
            .split(" ")
            .slice(0, 2)
            .map((w) => w[0])
            .join("")
            .toUpperCase();
    };

    const errors = useMemo(() => {
        const e = {};
        if (!form.tenant_id) e.tenant_id = "Company is required";
        if (companySelected) {
            // phoneNumber: required, must be string of 10 digits
            if (!form.phoneNumber || typeof form.phoneNumber !== "string" || !/^[0-9]{10}$/.test(form.phoneNumber)) {
                e.phoneNumber = "Phone number must be exactly 10 digits";
            }
            // password: required, min 8 chars
            if (!form.password || typeof form.password !== "string" || form.password.length < 8) {
                e.password = "Password must be at least 8 characters";
            }
        }
        return e;
    }, [form, companySelected]);

    const selectCompany = (company) => {
        setForm((prev) => ({ ...prev, tenant_id: company.id, identifier: company.identifier }));
        setCompanySelected(true);
        setSelectedLogoUrl(company.logo_url || null);
    };

    const handleCompanyChange = (e) => {
        const selectedIdentifier = e.target.value;
        const company = tenants.find((t) => t.identifier === selectedIdentifier);
        if (company) {
            selectCompany(company);
            setTouched((prev) => ({ ...prev, tenant_id: true }));
        } else {
            setCompanySelected(false);
            setSelectedLogoUrl(null);
            setForm((prev) => ({ ...prev, tenant_id: "", identifier: "" }));
        }
    };

    const handleImageSelect = (company) => {
        if (company) selectCompany(company);
    };

    const tenantLoginState = useSelector((state) => state.tenantLoginReducer || {});
    console.log("tenantLoginState", tenantLoginState);


    useEffect(() => {
        if (tenantLoginState.successData) {
            setSubmitting(false);
            toast.success("Logged In Successfully");
            // You can add localStorage logic here if needed
            const user = tenantLoginState.successData?.data?.user || tenantLoginState.successData?.data;
            const details = {
                id: user?.userId || user?.id || user?._id || null,
                name: user?.name || user?.fullName || null,
                mobileNumber: user?.phoneNumber || user?.mobile || user?.mobileNumber || null,
                token: user?.token || tenantLoginState.successData?.token || null,
                role: user?.role || null,
            };

            localStorage.setItem('userDetails', JSON.stringify(details));
            dispatch(clearTenantLoginAction());
                if (details.role === "ADMIN" || details.role === "ZM" || details.role === "AE") {
                    navigate("/masterData/dashboard");
                } else if (details.role === 'RM' || details.role === 'AM') {
                    navigate("/amrm");
                } else if (details.role === 'SO' || details.role === 'MDO') {
                    navigate("/SOorMDO/dashboard");
                } else {
                    navigate("/");
                }
        }
        if (tenantLoginState.error) {
            setSubmitting(false);
            const msg = tenantLoginState.error?.message || tenantLoginState.error?.error || 'Login failed. Please check your credentials.';
            toast.error(msg);
        }
        // No cleanup needed here; removed to prevent infinite update loop
    }, [tenantLoginState, dispatch, navigate]);

    const onSubmit = (e) => {
        e.preventDefault();
        setTouched({ identifier: true, phoneNumber: true, password: true });
        if (Object.keys(errors).length > 0) return;
        setSubmitting(true);
        dispatch(tenantLoginAction({
            identifier: form.identifier, // backend expects 'identifier' instead of 'tenant_id'
            mobile: form.phoneNumber, // backend expects 'mobile' as a string
            password: form.password,
        }));
    };

    const textFieldStyle = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "2px",
            alignItems: "center",
            width: "320px",
            maxWidth: "550px",
        },
        "& .MuiInputAdornment-root": {
            "& .MuiIconButton-root": {
                color: "#000",
                alignItems: "center",
            },
        },
    };

    useEffect(() => {
        const userDetails = JSON.parse(localStorage.getItem("userDetails"));
        if (userDetails?.token) {
            console.log("User already logged in, redirecting based on role:", userDetails);
            if (
                userDetails.role === "ADMIN" ||
                userDetails.role === "ZM" ||
                userDetails.role === "AE"
            ) {
                navigate("/masterData/dashboard");
            } else if (
                    userDetails.role === 'RM' || userDetails.role === 'AM'
            ) {
                    navigate("/amrm");
            } else if (
                userDetails.role === 'SO' || userDetails.role === 'MDO'
            ) {
                navigate("/SOorMDO/dashboard");
            } else {
                navigate("/login");
            }
        }
    }, [navigate]);

    return (
        <Box
            sx={{
                display: "flex",
                mt: 10,
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Paper
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row", md: "row" },
                    justifyContent: "center",
                    width: { xs: "90%", md: "900px", sm: "600px" },
                    maxWidth: "900px",
                    borderRadius: "15px",
                    overflow: "hidden",
                    boxShadow: 3,
                }}
            >
                {companySelected && (
                    <Box
                        sx={{
                            width: { xs: "100%", sm: "50%", md: "50%" },
                            ml: "-6%",
                            backgroundColor: "#E2F0FF",
                            display: { xs: "none", sm: "flex", md: "flex" },
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            pb: 5,
                        }}
                    >
                        {selectedLogoUrl ? (
                            <img
                                src={selectedLogoUrl}
                                alt="Login Illustration"
                                style={{ width: "60%" }}
                                onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.style.display = "none";
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: "60%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        borderRadius: "50%",
                                        backgroundColor: "#3A349B",
                                        color: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: 700,
                                        fontSize: 36,
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {getInitials(
                                        tenants.find((t) => t.id === form.tenant_id)?.companyName ||
                                        tenants.find((t) => t.id === form.tenant_id)?.identifier
                                    )}
                                </Box>
                            </Box>
                        )}
                        <Typography
                            sx={{ textAlign: "center", mt: 2, color: "#000", fontWeight: "bold", width: "60%" }}
                        >
                            Access your dashboard in just a few clicks.
                        </Typography>
                        <Typography sx={{ textAlign: "center", mt: 1, color: "#000", width: "80%", fontSize: "14px" }}>
                            Connect your bank card, and create accounts in the selected currency.
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        padding: { xs: "40px", sm: "40px", md: "40px" },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        {companySelected && (
                            <Button
                                sx={{ color: "#3A349B", mr: "-15%" }}
                                onClick={() => {
                                    setCompanySelected(false);
                                    setForm((prev) => ({ ...prev, tenant_id: "" }));
                                }}
                            >
                                <CloseIcon />
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ mb: 3, textAlign: { xs: "left", sm: "center" } }}>
                        <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "#31303b", mb: 1 }}>
                            Famer 360
                        </Typography>

                        <Typography sx={{ color: "gray" }}>Please login to access your account.</Typography>
                    </Box>

                    <Box
                        sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", justifyContent: "center" }}
                    >
                        <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                            {!companySelected && (
                                tenantsLoading ? (
                                    <Box sx={{ display: "flex", alignItems: "center", minHeight: 56, pl: 0.5 }}>
                                        <CircularProgress size={22} sx={{ mr: 1.5 }} />
                                        <Typography color="text.secondary">Loading companies…</Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <TextField
                                            select
                                            margin="dense"
                                            id="tenant_id"
                                            name="tenant_id"
                                            label="Select Company"
                                            variant="outlined"
                                            size="small"
                                            value={tenants.find((t) => t.id === form.tenant_id)?.identifier || ""}
                                            onChange={handleCompanyChange}
                                            onBlur={() => setTouched((prev) => ({ ...prev, tenant_id: true }))}
                                            error={touched.tenant_id && Boolean(errors.tenant_id)}
                                            helperText={touched.tenant_id && errors.tenant_id}
                                            sx={{ ...textFieldStyle }}
                                        >
                                            {tenants && Array.isArray(tenants) && tenants.length > 0 ? (
                                                tenants.map((tenant) => (
                                                    <MenuItem key={tenant.id} value={tenant.identifier}>
                                                        {tenant.companyName || tenant.identifier}
                                                    </MenuItem>
                                                ))
                                            ) : (
                                                <MenuItem disabled>No companies found</MenuItem>
                                            )}
                                        </TextField>

                                        {tenantsError && (
                                            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                                                Failed to load companies.
                                            </Typography>
                                        )}

                                        {tenants && Array.isArray(tenants) && tenants.length > 0 && (
                                            <Box
    sx={{
        width: "100%",
        mt: 2,
        mb: 2,
        ...(tenants.length > 3          
            ? { height: 220, overflowY: "auto" }
            : {}),
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start"       
    }}
>
    <Box
        sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(3, 1fr)" },
            gap: 1.5,
            mt: 1,            
            justifyItems: "center",
            alignItems: "center",
            maxWidth: 600,
            width: "100%",
            mx: "auto",
        }}
    >
                                                    {tenants.map((tenant) => (
                                                        <Box
                                                            sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center" }}
                                                            key={tenant.id}
                                                        >
                                                            <Button
                                                                variant="outlined"
                                                                onClick={() => handleImageSelect(tenant)}
                                                                sx={{
                                                                    textTransform: "none",
                                                                    border: "none",
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    minHeight: 110,
                                                                }}
                                                            >
                                                                {tenant.logo_url ? (
                                                                    <img
                                                                        src={tenant.logo_url}
                                                                        alt={tenant?.companyName}
                                                                        onError={(e) => {
                                                                            e.currentTarget.onerror = null;
                                                                            e.currentTarget.style.display = "none";
                                                                        }}
                                                                        style={{
                                                                            width: 84,
                                                                            height: 84,
                                                                            padding: 2,
                                                                            objectFit: "contain",
                                                                            border: "1px solid #3A349B",
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div
                                                                        style={{
                                                                            width: 84,
                                                                            height: 84,
                                                                            borderRadius: "50%",
                                                                            backgroundColor: "#3A349B",
                                                                            color: "#fff",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            fontWeight: 600,
                                                                            fontSize: 18,
                                                                            textTransform: "uppercase",
                                                                        }}
                                                                    >
                                                                        {tenant?.companyName
                                                                            ? getInitials(tenant.companyName)
                                                                            : getInitials(tenant.identifier)}
                                                                    </div>
                                                                )}
                                                            </Button>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ mt: 1, maxWidth: 100, display: "flex", justifyContent: "center", textAlign: "center" }}
                                                            >
                                                                {tenant.companyName || tenant.identifier}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </>
                                )
                            )}

                            {companySelected && (
                                <>
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        label="Phone Number"
                                        type="text"
                                        variant="outlined"
                                        size="small"
                                        value={form.phoneNumber}
                                        onChange={(e) => setForm((prev) => ({ ...prev, phoneNumber: e.target.value.replace(/[^0-9]/g, "") }))}
                                        onBlur={() => setTouched((prev) => ({ ...prev, phoneNumber: true }))}
                                        inputProps={{ maxLength: 10 }}
                                        inputMode="numeric"
                                        error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                                        helperText={touched.phoneNumber && errors.phoneNumber}
                                        sx={textFieldStyle}
                                    />
                                    <TextField
                                        margin="dense"
                                        id="password"
                                        name="password"
                                        label="Password"
                                        type={showPassword ? "text" : "password"}
                                        variant="outlined"
                                        size="small"
                                        value={form.password}
                                        onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                                        onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
                                        error={touched.password && Boolean(errors.password)}
                                        helperText={touched.password && errors.password}
                                        sx={textFieldStyle}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton aria-label="toggle password visibility" onClick={handleTogglePassword} edge="end">
                                                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-start", mb: 2 }}>
                                        <Button
                                            style={{ textDecoration: "none", color: "#3A349B", padding: "0px" }}
                                            onClick={() => navigate('/forgotPassword')}
                                        >
                                            Forgot Password?
                                        </Button>
                                    </Box>
                                    <Button
                                        type="submit"
                                        disabled={submitting}
                                        sx={{
                                            backgroundColor: "#3A349B",
                                            color: "#fff",
                                            borderRadius: "15px",
                                            p: "10px",
                                            width: "100%",
                                            maxWidth: "300px",
                                            mb: 2,
                                            "&:disabled": { opacity: 0.8 },
                                        }}
                                    >
                                        {submitting ? (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", color: "#fff" }}>
                                                <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
                                                Logging in…
                                            </Box>
                                        ) : (
                                            "Login"
                                        )}
                                    </Button>
                                    <Button
                                        sx={{
                                            textTransform: "none",
                                            alignSelf: "center",
                                            marginLeft: { xs: "0", sm: "-10%", md: "-20%", lg: "-5%" },
                                            color: "gray",
                                            marginBottom: "10px",
                                            padding: "0px",
                                        }}
                                        onClick={() => navigate('/otpLogin')}
                                    >
                                        Login with ? <span style={{ color: "#66BCE8" }}>OTP</span>
                                    </Button>
                                </>
                            )}
                        </form>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default LoginModel;